/**
 * Plausible Analytics helper (self-hosted, ТД-22).
 *
 * Тонкая type-safe обёртка над window.plausible(). Скрипт грузится в
 * index.html (stats.storica.coach). Cookieless, без PII — в props кладём
 * ТОЛЬКО enum/bool/число, никогда email/имя/id пользователя.
 *
 * Имена событий — через enum PlausibleEvent, чтобы исключить опечатки и
 * рассинхрон с server-side событиями (api/services/plausible.py).
 */

// const-object вместо enum: erasable-совместимо, единый стиль с admin.
export const PlausibleEvent = {
  SignupStarted: "signup_started",
  SignupCompleted: "signup_completed",
  TrialStarted: "trial_started",
  TrialToPaid: "trial_to_paid",
  TrialExpiredNoPaid: "trial_expired_no_paid",
  PaidConversion: "paid_conversion",
  SubscriptionCancelled: "subscription_cancelled",
  FeatureUsed: "feature_used",
  FoundingApplicationSubmitted: "founding_application_submitted",
  KbArticleOpened: "kb_article_opened",
  // H4 — activation / lifecycle funnel.
  FirstWorkoutLogged: "first_workout_logged",
  CoachLinkAccepted: "coach_link_accepted",
  // Q3-b — athlete explicitly declined invite (distinct from
  // "ignored until expiry" — coach dashboard surfaces both states).
  InviteDeclined: "invite_declined",
  TwoFAEnabled: "2fa_enabled",
  DSARRequested: "dsar_requested",
  // W1.5 — public landing telemetry. Cookieless, no PII; props carry
  // enum/bool only (lang, source, returning, sport, cta_key, role).
  // Wired in athlete/src/pages/LandingPage.tsx and (mirror) admin
  // CoachLandingPage. Server-side enum sync not needed — these events
  // fire client-side only.
  LandingViewed: "landing_viewed",
  LandingSportOpened: "landing_sport_opened",
  LandingCtaClicked: "landing_cta_clicked",
  LandingLanguageSwitched: "landing_language_switched",
  // A4 (Шаг #3) — per-question questionnaire funnel. Cookieless. Props
  // carry question_id (a stable slug from schema, NOT PII), section,
  // duration in seconds, and value_kind (prefilled / edited_prefill /
  // manual). These power a Plausible heatmap of where athletes drop off.
  QuestionFocused: "q_focused",
  QuestionCompleted: "q_completed",
  QuestionSkipped: "q_skipped",
  // `q_abandoned` fires only on a true browser exit (beforeunload) — the
  // athlete closed the tab / hit back to the OS / put the phone away.
  // `q_left_form` fires on a SPA route change away from the questionnaire
  // (nav to dashboard, settings, etc). Same props shape; separating them
  // keeps the abandonment funnel clean — a route change is NOT the same
  // as quitting.
  QuestionnaireAbandoned: "q_abandoned",
  QuestionnaireLeftForm: "q_left_form",
  // W3.21 (Шаг #6) — dashboard "new questions added" nudge.
  // Props carry schema_version (int) and reason (slug). Lets the
  // dashboard funnel measure shown → cta-clicked → completed-fill.
  NeedsReviewBannerShown: "needs_review_shown",
  NeedsReviewBannerCta: "needs_review_cta",
  NeedsReviewBannerDismissed: "needs_review_dismissed",
  // W3.18 (Шаг #7) — coach-flagged question seen by athlete. Fires once
  // per (qid + session) — we dedup on the client so a question staying
  // visible across re-renders doesn't spam the funnel.
  // Pair with admin-side `coach_flag_question` event to measure the loop
  // "coach flagged → athlete saw → athlete edited (auto-unflag)".
  ViewFlaggedQuestion: "view_flagged_question",
  // A7 (Шаг #10) — pre-questionnaire «what we already know» modal.
  // Props carry field_count (int), source_count (int), sport_hint
  // ("sport.run" | null), intervals_connected (bool). The funnel
  // measures shown → confirm (Start filling) vs shown → dismiss.
  PrefillModalShown: "prefill_modal_shown",
  PrefillModalConfirmed: "prefill_modal_confirmed",
  PrefillModalDismissed: "prefill_modal_dismissed",
  // A10 (Шаг #12) — pre-submit summary modal. Props: answered (int),
  // skipped (int), missing (int), sport ("sport.run" | "unknown").
  // Funnel: shown → confirmed (questionnaire actually submitted) vs
  // shown → returned_to_edit (athlete bounced back to form).
  SummaryModalShown: "summary_modal_shown",
  SummaryModalConfirmed: "summary_modal_confirmed",
  SummaryModalReturnedToEdit: "summary_modal_returned_to_edit",
  // Шаг #13 — athlete-side coach edit visibility (drawer + banner).
  // Funnel: BannerShown → (Clicked | Dismissed); HistoryOpened tracks
  // direct-link clicks too (button outside the banner). Props:
  // revision_id (int) for banner correlation; revisions_count for
  // drawer correlation; reason_length (int) для understanding tonality.
  CoachEditBannerShown: "coach_edit_banner_shown",
  CoachEditBannerClicked: "coach_edit_banner_clicked",
  CoachEditBannerDismissed: "coach_edit_banner_dismissed",
  AthleteHistoryOpened: "athlete_history_opened",
  // Cycle 15.A — burnout-risk banner telemetry. Props:
  //   band: "yellow" | "amber" | "red"
  //   layers_contributing: int (0..3) — how many of the 3 layers
  //     scored in the burnout range (≤ -0.10). Lets us tell apart
  //     single-layer noise from multi-layer signal in the funnel.
  // Fired once per (band, day) — dedup via the same key as the
  // dismiss-state to avoid spamming on every re-render.
  BurnoutBannerShown: "burnout_banner_shown",
  BurnoutBannerDismissed: "burnout_banner_dismissed",
  // target: "wellness" | "coach" — which CTA the athlete tapped.
  BurnoutBannerCta: "burnout_banner_cta",
} as const;

export type PlausibleEvent = (typeof PlausibleEvent)[keyof typeof PlausibleEvent];

/** Допустимые имена фич для feature_used (закрытый список — без свободного текста). */
export type FeatureName =
  | "threshold_test"
  | "plan_select"
  | "goal_set"
  | "fitness_curve_viewed";

type PropValue = string | number | boolean;

interface PlausibleOptions {
  props?: Record<string, PropValue>;
}

type PlausibleFn = (event: string, options?: PlausibleOptions) => void;

declare global {
  interface Window {
    plausible?: PlausibleFn & { q?: unknown[] };
  }
}

/** Низкоуровневый трек. Безопасен на SSR и до загрузки скрипта (queue).
 *
 * Dev-mode no-op: every HMR reload would otherwise fire the cleanup
 * effects (q_left_form / q_abandoned) and pollute the Plausible aggregates
 * with developer events. We log to console instead so the wiring stays
 * verifiable in dev.
 */
export function trackEvent(
  name: PlausibleEvent,
  props?: Record<string, PropValue>,
): void {
  if (typeof window === "undefined") return;
  // Skip ONLY `vite dev` HMR sessions — they fire teardown effects on
  // every save and would otherwise pollute the Plausible aggregates with
  // developer events. `MODE === "test"` (vitest) and "production" both
  // fall through to the real tracker — tests assert that the call
  // actually reaches window.plausible.
  if (import.meta.env.MODE === "development") {
    // eslint-disable-next-line no-console
    console.debug("[plausible:dev]", name, props);
    return;
  }
  const plausible = window.plausible;
  if (typeof plausible === "function") {
    plausible(name, props ? { props } : undefined);
  }
}

/** Удобный шорткат для feature_used с проверенным именем фичи. */
export function trackFeature(feature: FeatureName): void {
  trackEvent(PlausibleEvent.FeatureUsed, { feature_name: feature });
}

/** Открытие статьи KB. slug — не PII, это публичный идентификатор статьи. */
export function trackKbArticleOpened(slug: string): void {
  trackEvent(PlausibleEvent.KbArticleOpened, { slug });
}
